import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Users, ChevronRight } from 'lucide-react';

/**
 * DashboardGroupsCard — quiet, contextual entry point to private prayer groups.
 *
 * • Renders NOTHING if the user isn't in any groups (keeps the dashboard clean).
 * • With ONE group  → a single "Open <group>" button.
 * • With 2+ groups  → a compact list of chevron rows, sized so it never dominates
 *   the surrounding cards.
 */
const DashboardGroupsCard = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/groups/my`);
        if (!cancelled) setGroups(res.data || []);
      } catch {
        // Silent — if the call fails we just don't render.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API]);

  if (loading || groups.length === 0) return null;

  if (groups.length === 1) {
    const g = groups[0];
    return (
      <div className="mt-3" data-testid="dashboard-groups-card">
        <Button
          onClick={() => navigate(`/groups/${g.id}`)}
          className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg hover:shadow-xl transition-all"
          data-testid={`dashboard-open-group-${g.id}`}
        >
          <div className="flex items-center justify-center gap-3">
            <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <Users className="!w-5 !h-5" />
            </span>
            <span className="text-lg font-semibold truncate">Open {g.name}</span>
          </div>
        </Button>
      </div>
    );
  }

  return (
    <div
      className="mt-3 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-white overflow-hidden"
      data-testid="dashboard-groups-card"
    >
      <div className="px-4 py-2.5 flex items-center gap-2 border-b border-emerald-200/60 bg-white/50">
        <Users className="w-4 h-4 text-emerald-700" />
        <span className="text-sm font-semibold text-emerald-900">My Prayer Groups</span>
        <span className="ml-auto text-xs text-emerald-700/70">
          {groups.length} group{groups.length === 1 ? '' : 's'}
        </span>
      </div>
      <ul className="divide-y divide-emerald-100">
        {groups.map((g) => (
          <li key={g.id}>
            <button
              type="button"
              onClick={() => navigate(`/groups/${g.id}`)}
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-white/70 transition-colors text-left"
              data-testid={`dashboard-open-group-${g.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{g.name}</p>
                <p className="text-xs text-slate-500">
                  {(g.members?.length ?? g.member_count ?? 0)} members
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default DashboardGroupsCard;
